import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Users, ShieldCheck, Check, CalendarDays, Loader2, Crown, Wallet, CreditCard, Clock } from "lucide-react";
import { formatNaira, getStartingPrice, type RoomType } from "@/data/venues";
import { venuesApi } from "@/lib/api";
import { VenueService } from "@/services/venue.service";
import { BookingService } from "@/services/booking.service";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { TestModeBanner } from "@/components/TestModeBanner";

/* BACKEND: GET /api/v1/venues/:venueId — Loader fetches venue detail before rendering.
   Also uses POST /api/v1/bookings when user confirms payment.
   See BookingService.createBooking() → bookingsApi.create() */
export const Route = createFileRoute("/booking/$venueId")({
  validateSearch: (search: Record<string, unknown>) => ({
    roomTypeId: (search.roomTypeId as string) || undefined,
  }),
  loader: async ({ params }) => {
    const venue = await VenueService.getVenueById(params.venueId);
    if (!venue) throw notFound();
    return { venue };
  },
  component: BookingPage,
});

const STEPS = ["Configure", "Review", "Payment"] as const;

function BookingPage() {
  const { venue } = Route.useLoaderData();
  const { roomTypeId } = Route.useSearch();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const hasRoomTypes = venue.roomTypes && venue.roomTypes.length > 0;
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(roomTypeId || null);
  const selectedRoom = venue.roomTypes?.find((r: RoomType) => r.id === selectedRoomId) || null;
  const unitPrice = selectedRoom ? selectedRoom.price : venue.price;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: "/login", search: { redirect: `/booking/${venue.id}` } });
    }
  }, [isAuthenticated, navigate, venue.id]);

  const toDateStr = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const tomorrow = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() + 1); return d; }, [today]);
  const [fromVal, setFromVal] = useState<string>(() => toDateStr(today));
  const [toVal, setToVal] = useState<string>(() => toDateStr(tomorrow));
  const [guests, setGuests] = useState<number | "">(2);
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "bank_transfer">("bank_transfer");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [availability, setAvailability] = useState<{ date: string; status: string; roomTypeId: string | null }[]>([]);
  const [paymentDeadline, setPaymentDeadline] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(30 * 60 * 1000);

  useEffect(() => {
    venuesApi
      .availability(venue.id, { roomTypeId: selectedRoomId ?? undefined })
      .then((res) => setAvailability(res))
      .catch(() => setAvailability([]));
  }, [venue.id, selectedRoomId]);

  const blockedDates = useMemo(() =>
    availability
      .filter((a) => a.status === "blocked" || a.status === "booked")
      .map((a) => new Date(a.date)),
    [availability]
  );
  const checkIn = fromVal;
  const checkOut = toVal;

  const range = useMemo(() => ({
    from: new Date(fromVal),
    to: new Date(toVal),
  }), [fromVal, toVal]);

  const isOverlapping = useMemo(() => {
    if (!fromVal || !toVal) return false;
    const t0 = new Date(fromVal).getTime();
    const t1 = new Date(toVal).getTime();
    return blockedDates.some((d) => {
      const t = d.getTime();
      return t >= t0 && t < t1; // checkout date is exclusive — matches backend logic
    });
  }, [blockedDates, fromVal, toVal]);

  const nights = useMemo(() => {
    const a = new Date(checkIn).getTime();
    const b = new Date(checkOut).getTime();
    return Math.max(1, Math.round((b - a) / 86_400_000));
  }, [checkIn, checkOut]);

  const subtotal = unitPrice * nights;
  const total = subtotal;

  const next = () => {
    if (step === 0) {
      if (!range?.from || !range?.to) { toast.error("Pick check-in and check-out dates"); return; }
      if (isOverlapping) { toast.error("Your range overlaps an unavailable date"); return; }
      if (!guests || Number(guests) < 1) { toast.error("Please enter a valid number of guests"); return; }
    }
    const newStep = Math.min(STEPS.length - 1, step + 1);
    setStep(newStep);
    if (newStep === 2) {
      setPaymentDeadline(Date.now() + 30 * 60 * 1000);
    }
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  useEffect(() => {
    if (isAuthenticated) {
      import("@/lib/api").then(({ walletApi }) => {
        walletApi.getWallet().then(w => setWalletBalance(w.balance)).catch(() => setWalletBalance(null));
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (paymentDeadline === null) return;
    const interval = setInterval(() => {
      const rem = Math.max(0, paymentDeadline - Date.now());
      setRemainingMs(rem);
      if (rem <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [paymentDeadline]);

  const isExpired = remainingMs <= 0;
  const remMins = Math.floor(remainingMs / 60_000);
  const remSecs = Math.floor((remainingMs % 60_000) / 1000);

  const onPay = async () => {
    setSubmitting(true);
    try {
      const booking = await BookingService.createBooking({
        venueId: venue.id,
        roomTypeId: selectedRoomId ?? undefined,
        checkIn,
        checkOut,
        guests: Number(guests),
        totalPrice: subtotal, // backend validates against base price only
        customerName: user?.name || "Guest",
        customerEmail: user?.email || "",
      });

      const payment = await BookingService.pay(booking.id, paymentMethod);

      navigate({
        to: "/booking/success",
        search: {
          venue: venue.id,
          ref: payment.bookingRef,
          total: payment.amount,
          bookingId: booking.id,
          paymentStatus: payment.paymentStatus,
          bankName: payment.dedicatedBank?.bankName ?? "",
          accountName: payment.dedicatedBank?.accountName ?? "",
          accountNumber: payment.dedicatedBank?.accountNumber ?? "",
          paymentDeadline: payment.paymentDeadline ?? "",
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Payment failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <div className="mx-auto max-w-md space-y-6">
          <h1 className="font-display text-2xl font-bold text-stone-900">Sign in to Book</h1>
          <p className="text-stone-500">You need to be logged in to make a reservation. Please sign in or create an account to continue.</p>
          <div className="flex justify-center gap-3">
            <Link to="/login" className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition">
              Sign In
            </Link>
            <Link to="/register" className="inline-flex items-center justify-center rounded-xl border-2 border-stone-200 px-6 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition">
              Create Account
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      {/* Stepper */}
      <ol className="mb-10 flex items-center justify-center gap-3 sm:gap-6">
        {STEPS.map((label, i) => (
          <li key={label} className="flex items-center gap-3">
            <div className={`grid size-8 place-items-center rounded-full text-sm font-semibold ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {i < step ? <Check className="size-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
            {i < STEPS.length - 1 && <span className="hidden h-px w-12 bg-border sm:inline-block" />}
          </li>
        ))}
      </ol>

      <div className="grid gap-8 md:grid-cols-[1fr_300px] lg:grid-cols-[1fr_380px]">
        <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-soft md:self-start lg:self-start">
          {step === 0 && (
            <div className="space-y-6">
              {/* Room Type Selector */}
              {hasRoomTypes && (
                <div>
                  <h2 className="font-display text-2xl font-semibold">Select room type</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Choose the room tier for your stay.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {venue.roomTypes!.map((rt: RoomType) => {
                      const isSelected = selectedRoomId === rt.id;
                      const coverImage = rt.images && rt.images.length > 0 ? rt.images[0] : null;
                      return (
                        <button
                          key={rt.id}
                          type="button"
                          onClick={() => setSelectedRoomId(rt.id)}
                          className={`relative flex overflow-hidden rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? "border-primary bg-primary/5 shadow-soft"
                              : "border-border/60 bg-card hover:border-primary/30"
                          }`}
                        >
                          {/* Thumbnail */}
                          {coverImage && (
                            <div className="relative w-24 shrink-0 overflow-hidden">
                              <img src={coverImage} alt={rt.name} className="size-full object-cover" />
                            </div>
                          )}
                          <div className="flex flex-1 flex-col p-3">
                            {isSelected && (
                              <div className="absolute top-2 right-2 grid size-5 place-items-center rounded-full bg-primary text-primary-foreground z-10">
                                <Check className="size-3" />
                              </div>
                            )}
                            <div className="flex items-center gap-1.5">
                              <Crown className={`size-3.5 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                              <span className="text-sm font-semibold">{rt.name}</span>
                            </div>
                            <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-1">{rt.description}</p>
                            <div className="mt-auto pt-1.5 flex items-center justify-between">
                              <span className="font-display text-sm font-bold">{formatNaira(rt.price)}<span className="text-xs font-normal text-muted-foreground"> /night</span></span>
                              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Users className="size-2.5" /> {rt.maxGuests}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-display text-2xl font-semibold">Choose your dates</h2>
                <p className="mt-1 text-sm text-muted-foreground">Select your check-in and check-out dates below.</p>
              </div>

              {/* Date selectors wrapped in a highly snug, premium card matching the size of a calendar picker */}
              <div className="max-w-md space-y-6">
                <div className="rounded-xl border border-border bg-background/50 p-5 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="checkin" className="font-semibold text-xs text-muted-foreground flex items-center gap-1.5">
                        <CalendarDays className="size-3.5 text-primary" /> Check-in (From)
                      </Label>
                      <Input
                        id="checkin"
                        type="date"
                        min={toDateStr(today)}
                        value={fromVal}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFromVal(val);
                          // If checkout is before or equal to checkin, push checkout to checkin + 1 day
                          if (!toVal || toVal <= val) {
                            const [y, m, d] = val.split("-").map(Number);
                            const nextD = new Date(y, m - 1, d);
                            nextD.setDate(nextD.getDate() + 1);
                            setToVal(toDateStr(nextD));
                          }
                        }}
                        className="w-full bg-card border border-border/80 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="checkout" className="font-semibold text-xs text-muted-foreground flex items-center gap-1.5">
                        <CalendarDays className="size-3.5 text-primary" /> Check-out (To)
                      </Label>
                      <Input
                        id="checkout"
                        type="date"
                        min={(() => {
                          const [y, m, d] = fromVal.split("-").map(Number);
                          const nextD = new Date(y, m - 1, d);
                          nextD.setDate(nextD.getDate() + 1);
                          return toDateStr(nextD);
                        })()}
                        value={toVal}
                        onChange={(e) => setToVal(e.target.value)}
                        className="w-full bg-card border border-border/80 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Overlap Warning */}
                {isOverlapping && (
                  <div className="flex items-start gap-2.5 rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-sm text-destructive">
                    <span className="font-semibold">⚠️ Already Booked:</span> Your selected range overlaps with dates that are already reserved. Please select another date.
                  </div>
                )}

                {/* Booked Dates Reference */}
                <div className="rounded-xl border border-border/40 bg-accent/30 p-4">
                  <h3 className="font-display text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <span className="inline-block size-2 rounded-full bg-primary" /> Booked Dates Reference
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">The following dates are already booked for this venue:</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {blockedDates.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">No upcoming booked dates. All dates available!</span>
                    ) : (
                      blockedDates.slice(0, 8).map((d) => (
                        <span key={d.toISOString()} className="inline-block rounded-lg bg-card border border-border/50 px-2 py-1 text-xs font-mono text-muted-foreground">
                          {d.toLocaleDateString("en-NG", { month: "short", day: "numeric" })}
                        </span>
                      ))
                    )}
                    {blockedDates.length > 8 && (
                      <span className="text-xs text-muted-foreground self-center">+{blockedDates.length - 8} more</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm border-t border-border pt-4">
                <span className="inline-flex items-center gap-2"><CalendarDays className="size-4 text-primary" /> {nights} night{nights !== 1 ? "s" : ""}</span>
                <span className="text-muted-foreground">{new Date(checkIn).toDateString()} → {new Date(checkOut).toDateString()}</span>
              </div>
              
              <div className="space-y-2 sm:max-w-xs">
                <Label htmlFor="g"><Users className="mr-1 inline size-3.5" /> Guests</Label>
                <Input
                  id="g"
                  type="number"
                  min={1}
                  max={20}
                  value={guests}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGuests(val === "" ? "" : Number(val));
                  }}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl font-semibold">Review your booking</h2>
              <div className="space-y-3 text-sm">
                <Row label="Venue" value={venue.name} />
                {selectedRoom && <Row label="Room type" value={selectedRoom.name} />}
                <Row label="Location" value={`${venue.location}, Oyo State`} />
                <Row label="Check-in" value={new Date(checkIn).toDateString()} />
                <Row label="Check-out" value={new Date(checkOut).toDateString()} />
                <Row label="Guests" value={String(guests || 1)} />
                <Row label="Nights" value={String(nights)} />
                <Row label="Rate per night" value={formatNaira(unitPrice)} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <TestModeBanner />
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold">Payment</h2>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold tracking-wide ${
                  isExpired ? "bg-rose-50 text-rose-600 border-rose-200" : remMins < 5 ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-blue-50 text-blue-600 border-blue-200"
                }`}>
                  <Clock className="size-3" />
                  {isExpired ? "Session expired" : `${remMins}:${remSecs.toString().padStart(2, "0")} left`}
                </span>
              </div>

              <div className="grid gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("wallet")}
                  className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                    paymentMethod === "wallet"
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-primary/30"
                  }`}
                >
                  <Wallet className="size-6 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Pay with Wallet</p>
                    <p className="text-xs text-muted-foreground">
                      {walletBalance !== null ? `Balance: ${formatNaira(walletBalance)}` : "Loading balance…"}
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("bank_transfer")}
                  className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                    paymentMethod === "bank_transfer"
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-primary/30"
                  }`}
                >
                  <CreditCard className="size-6 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Bank Transfer</p>
                    <p className="text-xs text-muted-foreground">Transfer to your dedicated virtual account</p>
                  </div>
                </button>
              </div>


              {paymentMethod === "wallet" && walletBalance !== null && walletBalance < total && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  Insufficient wallet balance. Please top up or choose bank transfer.
                </div>
              )}

              {paymentMethod === "bank_transfer" && (
                <div className="rounded-xl bg-accent/40 border border-border/50 p-4 space-y-2 text-sm">
                  <p className="font-medium">How it works:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>Click "Pay" to create your booking</li>
                    <li>Transfer {formatNaira(total)} to your dedicated virtual account</li>
                    <li>Your booking will be confirmed automatically once payment is received</li>
                  </ol>
                </div>
              )}

              {isExpired && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                  Your payment session has expired. Please go back and restart the booking process.
                </div>
              )}
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" /> Secure payment via AltPay.
              </p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={prev} disabled={step === 0}>Back</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next}>Continue</Button>
            ) : (
              <Button onClick={onPay} disabled={submitting || isExpired}>
                {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                Pay {formatNaira(total)}
              </Button>
            )}
          </div>
        </div>

        {/* Summary */}
        <aside className="md:sticky md:top-24 md:self-start lg:sticky lg:top-24 lg:self-start">
          <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
            <img src={venue.image} alt={venue.name} className="aspect-[16/10] w-full object-cover" />
            <div className="space-y-3 p-6">
              <div>
                <h3 className="font-display text-lg font-semibold">{venue.name}</h3>
                <p className="text-xs text-muted-foreground">{venue.location}, Oyo State</p>
              </div>
              <div className="space-y-2 border-t border-border pt-4 text-sm">
                {selectedRoom && (
                  <Row label="Room type" value={selectedRoom.name} />
                )}
                <Row label={`${formatNaira(unitPrice)} × ${nights} night${nights !== 1 ? "s" : ""}`} value={formatNaira(subtotal)} />
              </div>
              <div className="flex items-center justify-between border-t border-border pt-4 font-display text-lg font-bold">
                <span>Total</span>
                <span>{formatNaira(total)}</span>
              </div>
              <Link to="/venues/$venueId" params={{ venueId: venue.id }} className="block text-center text-xs text-muted-foreground hover:text-primary">
                View venue details
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}
